import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';

import {
  AbstractControl,
  FormBuilder,
  FormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';

import { translate } from '@jsverse/transloco';
import { Subject, takeUntil } from 'rxjs';
import { DropdownObject, EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { ChartOptionConstants, Constants } from 'app/constants/constants';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

import { ErrorObjectUtils } from 'app/utils/error-object.utils';

export interface BigNumberChartOptionsData {
  field: 'one' | 'two';
  displayName: string;
  isComparisonOrImpact: boolean;
  analyticItemType: 'documents' | 'long' | 'double' | 'rating';
  analyticItem: string;
  scoreRange: string;
  scoringStrategy: string;
  boxSize: '1' | '2' | '3' | '4' | '5' | '6' | '7';
  boxDirection: 'TOP' | 'BOTTOM';
  increaseColor: string;
  decreaseColor: string;
  showVsTotal: boolean;
  showDisplayName: boolean;
  displayDataAs: 'absolute' | 'percentage';
  intPrecision: '0' | '1' | '2';
}

@EnableClassTelemetry()
@Component({
  selector: 'app-big-number-chart-options',
  templateUrl: './big-number-chart-options.component.html',
  styleUrls: ['./big-number-chart-options.component.scss'],
  standalone: false,
})
export class BigNumberChartOptionsComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() data: BigNumberChartOptionsData;
  @Output() formChanged: EventEmitter<any> = new EventEmitter();

  analyticItemType: 'documents' | 'long' | 'double' | 'rating';
  displayGroups = [
    { id: 'AVERAGE', title: translate('widgets.options.average') },
    { id: 'SUM', title: translate('widgets.options.sum') },
  ];
  displayScoringStrategyRadios = true;
  displayRatingOptions = true;
  bigNumberOptionsForm: UntypedFormGroup;

  boxDirectionDos = ChartOptionConstants.BOXDIRECTIONDOS;
  boxSizeDos: DropdownObject[];

  readonly intPrecision: DropdownObject[] = [
    { title: '0', id: '0' },
    { title: '1', id: '1' },
    { title: '2', id: '2' },
  ];
  readonly comparisonRadios = [
    {
      label: translate('widgets.options.colors.gray'),
      value: 'gray',
    },
    {
      label: translate('widgets.options.colors.red'),
      value: 'red',
    },
    {
      label: translate('widgets.options.colors.green'),
      value: 'green',
    },
  ];
  maxTargetStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };

  private readonly unsub$: Subject<void> = new Subject<void>();

  constructor(private fb: FormBuilder) {}

  async ngOnInit() {
    this.analyticItemType = this.data.analyticItemType;
    this.boxSizeDos =
      this.data.boxDirection === 'BOTTOM'
        ? ChartOptionConstants.BOTTOMBOXSIZEDOS
        : ChartOptionConstants.TOPBOXSIZEDOS;
    this.buildForm();

    this.customizeForm();

    this.subscribeToForm();

    this.formChanged.emit(this.bigNumberOptionsForm);
    this.bigNumberOptionsForm.valueChanges.subscribe(() => {
      this.formChanged.emit(this.bigNumberOptionsForm);
    });
  }

  private calculateIntPrecisionDefaultValue() {
    if (
      this.analyticItemType === 'documents' ||
      (this.bigNumberOptionsForm?.value.scoringStrategyRadios != null &&
        this.bigNumberOptionsForm.value.scoringStrategyRadios === 'SUM')
    ) {
      return '0';
    } else {
      return '1';
    }
  }

  private customizeForm() {
    if (this.analyticItemType === 'rating') {
      this.displayGroups.push(
        { id: 'BOX', title: translate('widgets.options.box') },
        { id: 'NPS', title: translate('widgets.options.nps') }
      );
    }

    if (this.analyticItemType === 'documents') {
      this.bigNumberOptionsForm.get('scoringStrategyRadios').disable();
      this.displayScoringStrategyRadios = false;
    }
    if (
      this.analyticItemType === 'documents' ||
      this.analyticItemType === 'long' ||
      this.analyticItemType === 'double'
    ) {
      this.bigNumberOptionsForm.get('boxDirectionSelect').disable();
      this.bigNumberOptionsForm.get('boxSizeSelect').disable();
      this.bigNumberOptionsForm.get('maxTarget').disable();
      this.bigNumberOptionsForm.get('targetRatingToggle').disable();
      this.displayRatingOptions = false;
    }
  }

  private async buildForm() {
    const numberInputValidatorFactory = (status: ErrorObject): ValidatorFn => {
      return (control: AbstractControl) => {
        if (control.value > 1000 || control.value < 2) {
          ErrorObjectUtils.setError(
            status,
            translate('widgets.options.error-must-be-between', { min: 2, max: 1000 })
          );
          return { error: 'minmax' };
        }

        ErrorObjectUtils.setNoError(status);
        return null;
      };
    };

    const targetScale = (this.data.scoreRange ?? Constants.TWTRA_DEFAULT_DEPENDENT_RANGE).split(
      ':'
    );

    const targetMin = parseInt(targetScale[0], 10);
    const targetMax = parseInt(targetScale[1], 10);

    this.bigNumberOptionsForm = this.fb.group({
      scoringStrategyRadios: new FormControl(
        this.data.scoringStrategy ?? Constants.TWTRA_DEFAULT_SCORING_STRATEGY
      ),
      boxDirectionSelect: new FormControl({
        value: this.data.boxDirection ?? Constants.TWTRA_DEFAULT_BOX_DIRECTION,
        disabled: !this.data.boxDirection,
      }),
      boxSizeSelect: new FormControl({
        value: this.data.boxSize ?? Constants.TWTRA_DEFAULT_BOX_SIZE,
        disabled: !this.data.boxSize,
      }),
      intPrecisionSelect: new FormControl({
        value: this.data.intPrecision ?? this.calculateIntPrecisionDefaultValue(),
        disabled: false,
      }),
      maxTarget: [targetMax, numberInputValidatorFactory(this.maxTargetStatus)],
      targetRatingToggle: [targetMin],
      increaseRadios: [
        { value: this.data.increaseColor ?? 'gray', disabled: !this.data.isComparisonOrImpact },
      ],
      decreaseRadios: [
        { value: this.data.decreaseColor ?? 'gray', disabled: !this.data.isComparisonOrImpact },
      ],
      showDisplayName: [this.data.showDisplayName ?? true],
      displayDataAs: [
        {
          value:
            this.data.displayDataAs ??
            (this.data.analyticItemType === 'rating' ? 'percentage' : 'absolute'),
          disabled: !this.data.isComparisonOrImpact,
        },
      ],
    });
  }

  private subscribeToForm(): void {
    this.bigNumberOptionsForm
      .get('scoringStrategyRadios')
      .valueChanges.pipe(takeUntil(this.unsub$))
      .subscribe((value: string) => {
        if (value === 'BOX') {
          this.bigNumberOptionsForm.get('boxDirectionSelect').enable();
          this.bigNumberOptionsForm.get('boxSizeSelect').enable();
        } else {
          this.bigNumberOptionsForm.get('boxDirectionSelect').disable();
          this.bigNumberOptionsForm.get('boxSizeSelect').disable();
        }
        this.bigNumberOptionsForm
          .get('intPrecisionSelect')
          .patchValue(this.data.intPrecision ?? this.calculateIntPrecisionDefaultValue());
      });

    this.bigNumberOptionsForm
      .get('boxDirectionSelect')
      .valueChanges.pipe(takeUntil(this.unsub$))
      .subscribe((value: string) => {
        if (value === 'BOTTOM') {
          this.boxSizeDos = ChartOptionConstants.BOTTOMBOXSIZEDOS;
        } else {
          this.boxSizeDos = ChartOptionConstants.TOPBOXSIZEDOS;
        }

        this.bigNumberOptionsForm.patchValue({ boxSizeSelect: Constants.TWTRA_DEFAULT_BOX_SIZE });
      });
  }

  ngOnDestroy() {
    this.unsub$.next();
    this.unsub$.complete();
  }
}
